import React, { useMemo, useState } from 'react'
import { Box, Button, Switch, Dialog, DialogTitle, DialogContent, DialogActions, FormGroup, FormControlLabel, Typography } from '@mui/material'
import { getSkillBiteEmailHtml } from '@emerald-works-nova/email'
import { useSelector } from 'react-redux'
import { SkillBiteEditSlice, AppSlice } from '../../../../../reducers'
import { useTenant } from '@emerald-works-nova/auth'
import { buildToolkitUrl } from '../../../../../helpers/toolkit'
import { CONSTANTS } from '../../../../../util/constants'

const mockUserPayload = {
  username: 'mockCognitoUsername',
  email: 'mock@email.com',
  firstName: 'Mock User',
  commitment: 'Fake previous session commitment goes here, when we send the email we will use the real commitment. This is just a placeholder.'
}

const EmailPreviewModal = ({ open, setOpen, session }) => {
  const isFirstSession = session?.order === 1
  const skillBite = useSelector(SkillBiteEditSlice.selectors.selectSkillBite)

  const tenant = useTenant()
  const envInfo = useSelector(AppSlice.selectors.selectEnvInfo)
  const baseToolkitUrl = buildToolkitUrl(CONSTANTS.SB_PREVIEW_TENANT_KEY(envInfo.stage), envInfo.stage, envInfo.tld)

  const [hasPreviousSessionCommitment, setHasPreviousSessionCommitment] = useState(false)

  const emailHtml = useMemo(() => {
    if (!tenant || !session || !skillBite) return '<p>Nothing to display</p>'

    const { headline, intro, cta, commitmentCopy } = session?.emailConfig || {}

    const userEmailPayload = {
      ...mockUserPayload,
      tenantId: tenant.pk,
      commitment: mockUserPayload.commitment,
      commitmentCopy: hasPreviousSessionCommitment ? commitmentCopy?.replace('$$USER_COMMITMENT$$', mockUserPayload.commitment) : ''
    }

    const previousSessions = (skillBite?.sessions || [])
      .filter(s => s.order < session.order)
      .map(s => ({ name: s.name, url: `${baseToolkitUrl}/skill-bites/${skillBite.id}/${s.id}` }))

    const replaceWithDynamicValues = (text = '', userEmailPayload) => {
      return text
        .replace('$$USER_NAME$$', userEmailPayload.firstName)
        .replace('$$COMMITMENT_COPY$$', userEmailPayload?.commitmentCopy)
    }

    return getSkillBiteEmailHtml({
      tenantColor: tenant.mainColor || '#D4672B',
      tenantLogo: tenant.logo?.imgUrl || 'https://nova-live.imgix.net//tenantlogo-7d932a97-3200-4e36-a395-0c4436018c39.png?q=60&fm=webp&h=40',
      headline: replaceWithDynamicValues(headline, userEmailPayload),
      intro: replaceWithDynamicValues(intro, userEmailPayload),
      cta: replaceWithDynamicValues(cta, userEmailPayload),
      sessionUrl: `${baseToolkitUrl}/skill-bites/${skillBite.id}/${session.id}`,
      unsubscribeUrl: `${baseToolkitUrl}/skill-bites/${skillBite.id}?unsubscribe`,
      previousSessions
    })
  }, [tenant, session, skillBite, baseToolkitUrl, hasPreviousSessionCommitment])

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <Dialog
      open={open}
      PaperProps={{ style: { minWidth: '750px', backgroundColor: '#f5f5f5' } }}
    >
      <DialogTitle>
        <Box display='flex' flexDirection='row' justifyContent='space-between' alignItems='center'>
          <Typography variant='h6'>
            Email Preview
          </Typography>
          {(!isFirstSession && session?.emailConfig?.commitmentCopy) && (
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={hasPreviousSessionCommitment}
                    onChange={evt => setHasPreviousSessionCommitment(evt.target.checked)}
                  />
                }
                label='Last session commitment?'
              />
            </FormGroup>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <div
          dangerouslySetInnerHTML={{ __html: emailHtml }} style={{ backgroundColor: '#f5f5f5' }}
        />
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleClose} color='primary'>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EmailPreviewModal
